/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of H5Tracker
 */
import 'reflect-metadata';
import {
    ITracker,
    TrackedSuccessfulCallback,
    GetOmgIdCallback,
    TrackedHandler
} from 'com/didichuxing/tracker/ITracker';
import { IH5Context } from './IH5Tracker';
import { IPlanObject } from 'com/didichuxing/IPlanObject';
import {
    IReportingStrategyPlugin,
    IReportingStrategyPluginConstr,
    ITrackedEventArgs
} from 'com/didichuxing/tracker/plugin/IReportingStrategyPlugin';
import { IPlugin } from 'com/didichuxing/plugin/IPlugin';
import { IPluginCollection } from 'com/didichuxing/plugin/IPluginCollection';
import { ITrackerConfiguration } from 'com/didichuxing/tracker/config/ITrackerConfiguration';
import { IEnvConfigurationItem } from 'com/didichuxing/tracker/config/IEnvConfigurationItem';
import { IEnvConfigurationItemSync } from 'com/didichuxing/tracker/config/IEnvConfigurationItemSync';
import { IEnvConfigurationItemAsync } from 'com/didichuxing/tracker/config/IEnvConfigurationItemAsync';
import { IAttrs } from 'com/didichuxing/tracker/IAttrs';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { ITrackedEventData } from 'com/didichuxing/tracker/event/ITrackedEventData';
import { IGeoLocation } from 'com/didichuxing/tracker/geography/IGeoLocation';
import { IAutoClickCover } from 'com/didichuxing/tracker/IAutoClickCover';
import { IClickFilter } from 'com/didichuxing/tracker/IClickFilter';
import AH5EnvConfigSync from 'com/didichuxing/tracker/h5/envconfig/AH5EnvConfigSync';
import AppH5EnvConfig from 'com/didichuxing/tracker/h5/envconfig/AppH5EnvConfig';
import NormalH5EnvConfig from 'com/didichuxing/tracker/h5/envconfig/NormalH5EnvConfig';
import UniqueH5EnvConfig from 'com/didichuxing/tracker/h5/envconfig/UniqueH5EnvConfig';
import ReportCallbackUtil from 'com/didichuxing/tracker/plugin/utils/ReportCallbackUtil';
import SequencedEvent from 'com/didichuxing/tracker/plugin/events/SequencedEvent';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';
import GlobalUtil from 'com/didichuxing/tracker/plugin/utils/GlobalUtil';
import UUID from 'com/didichuxing/tracker/plugin/utils/UUID';
import PluginSet from 'com/didichuxing/tracker/PluginSet';
import NonH5Plugin from 'com/didichuxing/tracker/plugin/NonH5Plugin';
import OMGAppH5PageInitPlugin from 'com/didichuxing/tracker/plugins/OMGAppH5PageInitPlugin';
import OMGH5PageInitPlugin from 'com/didichuxing/tracker/plugins/OMGH5PageInitPlugin';
import OMGH5LogPlugin from 'com/didichuxing/tracker/plugins/OMGH5LogPlugin';

/**
 * JS脚本加载超时时间，单位 1000 ms
 * config (约 7.4kb)
 * [0.1,   0.5,   0.8,   0.9,  0.95,   0.99,   0.999]
 * [0.0, 150.1, 422.1, 649.8, 916.8, 3519.4, 10834.7]
 *
 * plugin (约 20.4kb)
 * [0.1,  0.5,   0.8,   0.9,  0.95,   0.99,   0.999]
 * [0.0, 54.4, 226.3, 494.4, 878.9, 3935.1, 14520.9]
 * @type {number}
 */
const SCRIPT_LOAD_TIMEOUT: number = 1000;
const PROTO = GlobalUtil.getProtocol();

/**
 * 默认的本地环境配置JS地址
 * @type {string}
 */
const DEFAULT_LOCAL_CONFIG_URL = PROTO + '{{cdnHost}}/static/{{cdnNamespace}}/{{version}}/omega.configs.min.js';

/**
 * 默认的国际化环境配置JS地址
 * @type {string}
 */
const DEFAULT_GLOBAL_CONFIG_URL = PROTO + '{{cdnGlobalHost}}/static/tracker_global/{{version}}/omega.configs.min.js';
/**
 * 欧洲环境配置地址
 */
const DEFAULT_DE_CONFIG_URL = PROTO + '{{cdnDeHost}}/static/tracker_de/{{version}}/omega.configs.min.js';
/**
 * Rlab配置js地址
 */
const DEFAULT_RLAB_CONFIG_URL = PROTO +
    '{{cdnRlabHost}}/static/falcon_public/tracker/{{version}}/omega.configs.min.js'
;
const OMG_VERSION: string = '{{version}}';
type ReportFucHandler = (eventArgs: ITrackedEventArgs) => Promise<ITrackedEvent>;

type MethodHandler = (...args: any[]) => any;
type ResolveHandler = MethodHandler;
type RejectHandler = (reason?: any) => void;

interface IMethodHolderItem {
    /**
     * @readonly
     * method handler
     */
    readonly method: MethodHandler;

    /**
     * @readonly
     * method 对应的参数。
     */
    readonly arguments: IArguments | any[];

    /**
     * @readonly
     * 异步回调方法。
     */
    readonly resolve?: ResolveHandler;

    /**
     * @readonly
     * 异步回调出错方法。
     */
    readonly reject?: RejectHandler;
}

/**
 * The MethodHold decorator
 * 针对返回void方法进行hold
 * @param {H5Tracker} target
 * @param {string} propertyName
 * @param {TypedPropertyDescriptor<T>} descriptor
 */
function MethodHold(target: H5Tracker, propertyName: string,
                    descriptor: PropertyDescriptor): void {
    const method: MethodHandler = descriptor.value;
    descriptor.value = function(...args: any[]) {
        if ((this as H5Tracker).getPluginConllection().size() <= 2) { // 2为同步插件的数量 plugin 未加载，需要把方法都hold
            (this as H5Tracker).holdMethod(method, arguments);
        } else {
            method.apply(this, arguments as any);
        }
    };
}

/**
 * The async MethodHold decorator
 * 针对返回Promise方法进行hold
 * @param {H5Tracker} target
 * @param {string} propertyName
 * @param {PropertyDescriptor} descriptor
 */
function MethodHoldAsync(target: H5Tracker, propertyName: string,
                         descriptor: PropertyDescriptor): void {
    const method: MethodHandler = descriptor.value;
    descriptor.value = function(...args: any[]) {
        if ((this as H5Tracker).getPluginConllection().size() <= 2) { // 2为同步插件的数量 plugin 未加载，需要把方法都hold
            return new Promise<any>(
                (resolve: (...args: any[]) => void, reject: (reason?: any) => void): void => {
                    (this as H5Tracker).holdMethod(method, arguments, resolve, reject);
                }
            );
        } else {
            return method.apply(this, arguments as any);
        }
    };
}

/**
 * The tracker for h5
 * @class
 * @implements {ITracker}
 */
export default class H5Tracker implements ITracker {
    /**
     * Omega已经实例化过的对象APPKEY集合名称
     */
    public static readonly OMG_INSTANTIATION_NAME: string = '__OMG_INSTANTIATION__';

    /**
     * Omega版本号
     */
    public static readonly OMG_VERSION: string = OMG_VERSION;

    /**
     * Omega异步环境配置集合对象名称。
     * @type {string}
     */
    public static readonly OMG_ASYNC_CONFIGS_NAME: string = '__OMG_ASYNC_CONFIGS_{{TRACKER_VERSION}}__';

    /**
     * Omega异步插件类型集合对象名称。
     * @type {string}
     */
    public static readonly OMG_ASYNC_PLUGINS_CONSTRS_NAME: string = '__OMG_ASYNC_PLUGINS_CONSTRS_{{TRACKER_VERSION}}__';

    /**
     * Omega同步插件类型集合对象名称。
     * @type {string}
     */
    public static readonly OMG_PLUGINS_CONSTRS_NAME: string = '__OMG_PLUGINS_CONSTRS_{{TRACKER_VERSION}}__';
    /**
     * onload之前错误列表
     */
    public beforeErrorList: ErrorEvent[];

    /**
     * The tracker context.
     */
    private context: IH5Context;

    /**
     * The plugin collection.
     */
    private pluginCollection: IPluginCollection;

    /**
     * The method handler chain.
     */
    private methodHolderChain: IMethodHolderItem[];

    /**
     * The method handler chain.
     */
    private methodHolderChainIsDone: boolean = false;

    /**
     * 自动点击上报覆盖对象
     */
    private clickedCover: IAutoClickCover = { attr: 'auto-track-click' };
    /**
     * 自动点击上报覆盖对象是否遍历子节点
     */
    private clickedCoverIsChild: boolean | undefined;

    /**
     * 自动点击上报过滤对象
     */
    private clickedFilter: IClickFilter | undefined;

    /**
     * 额外环境配置文件地址。
     */
    private extraConfigsUrl: string;

    /**
     * 获取omgId的回调
     */
    private getOmgIdHandler: GetOmgIdCallback | undefined;
    /**
     * viewportId名称
     * 具体值每个SDK初始化时放在自己内部
     * 因为每个SDK的UUID不应该共享 所以不挂在全局
     */
    private OMG_UWID_NAME: string = '';
    /**
     * trackHandler向attrs里注入额外参数
     */
    private trackHandlers: any[] = [];

    /**
     * @constructor
     * @param {ITrackerConfiguration} config
     */
    constructor(config: ITrackerConfiguration = {}) {
        this.methodHolderChain = [];
        this.beforeErrorList = [];
        this.context = this.getDefaultContext();
        /**
         * 插件的地址
         */
        if (config.uploadHost === 'omgup.1meal.net') {
            // 国际化外卖的上报地址
            this.extraConfigsUrl = this.getRlabDefaultConfigUrl();
        } else if (/global/.test(config.uploadHost || '')) {
            // 俄罗斯环境
            if (/omgup-ru\.didiglobal\.com/.test(config.uploadHost || '')) {
                console.error('该机房已迁移为欧洲机房');
                this.extraConfigsUrl = this.getDeDefaultConfigUrl();
            } else if (/omgup-de\.didiglobal\.com/.test(config.uploadHost || '')) {
                // 欧洲法拉克福
                this.extraConfigsUrl = this.getDeDefaultConfigUrl();
            } else {
                // 默认美东
                this.extraConfigsUrl = this.getGlobalDefaultConfigUrl();
            }
        } else {
            // 国内
            this.extraConfigsUrl = this.getLocalDefaultConfigUrl();
        }
        this.pluginCollection = new PluginSet();
        this.pagehideHandler = this.pagehideHandler.bind(this);
        this.initAsyncPluginCollection = this.initAsyncPluginCollection.bind(this);
        this.initSyncPluginCollection = this.initSyncPluginCollection.bind(this);
        this.setBeforeError = this.setBeforeError.bind(this);
        this.setConfig(config);
    }

    /**
     * 获取默认配置
     * @returns {IH5Context}
     */
    protected getDefaultContext(): IH5Context {
        let viewportId: string = this.OMG_UWID_NAME || '';
        if (!viewportId) {
            viewportId = UUID.v4;
            this.OMG_UWID_NAME = viewportId;
        }
        return {
            viewportId: viewportId,
            version: OMG_VERSION,
            url: window.location.href,
            utcOffset: -new Date().getTimezoneOffset(),
            userAgent: window.navigator.userAgent,
            timeout: 4000,
            isFirstSw: true
        };

    }

    /**
     * hold method handler
     * @param {MethodHandler} method
     * @param {IArguments | any[]} args
     */
    public holdMethod(method: MethodHandler, args: IArguments | any[],
                      resolve?: (...args: any[]) => void, reject?: (reason?: any) => void): void {
        this.methodHolderChain.push({
            method: method,
            arguments: args,
            resolve: resolve,
            reject: reject
        });
    }

    /**
     * pagehideHandler执行方法。
     */
    private pagehideHandler(): void {
        this.trackEvent(PluginUtils.EVENT_ID_PAGE_LEAVE);
        // 不进行销毁
        // setTimeout(() => {
        //     this.destroy();
        // }, 0);
    }

    /**
     * @override {ITracker#init()}
     * @inheritDoc
     */
    public init(config?: ITrackerConfiguration): void {
        // 针对config中的cookie进行提前设置
        if (config && config.useLocalOid) {
            this.context.useLocalOid = true;
        }
        this.initSyncPluginCollection();
        window.addEventListener('error', this.setBeforeError);
        window.removeEventListener('load', this.setBeforeError);
        // 部分框架全局trycatch，通过console.error收集错误
        this.proxyConsoleError();
        /**
         * 如果参数中有直接加载插件
         */
        if (config && config.fastLoad) {
            this.initAsyncPluginCollection();
        } else if (GlobalUtil.isLoaded()) { // window loaded
            this.initAsyncPluginCollection();
        } else {
            window.addEventListener('load', this.initAsyncPluginCollection);
        }
        // 在内嵌页面中发现不能进行pagehide事件
        window.addEventListener('pagehide', this.pagehideHandler);
    }

    /**
     * 代理console.error
     */
    private proxyConsoleError() {
        const t = this;
        // 关闭的话不进行监听
        if (t.context.autoConsoleError === false) {
            return;
        }
        if (console && typeof console === 'object') {
            try {
                const fn = console.error;
                console.error = function() {
                    try {
                        const errEvent = arguments[0];
                        if (errEvent && errEvent.message && errEvent.stack) {
                            t.setBeforeError({
                                type: 'error',
                                error: errEvent,
                                message: errEvent.message
                            });
                        }
                    } catch (error) {
                        console.info(`代理console.error失败,${error}`);
                    }
                    return fn.apply(this, arguments as any);
                };
            } catch (error) {
                console.info(`代理console.error失败,${error}`);
            }
        }
    }

    public setBeforeError(event: ErrorEvent | any): void {
        if (GlobalUtil.isLoaded()) {
            return;
        }
        if (event && event.type && event.type === 'error') {
            this.beforeErrorList.push(event);
        } else if (event && event.message && event.stack) {
            // 兼容console.error获取的错误信息
            this.beforeErrorList.push(event);
        }
        if (this.beforeErrorList.length > 15) {
            this.beforeErrorList.length = 10;
        }
    }

    /**
     * 本地默认配置url
     * @returns {string}
     */
    protected getLocalDefaultConfigUrl(): string {
        return DEFAULT_LOCAL_CONFIG_URL;
    }

    /**
     * 国际化默认配置url
     * @returns {string}
     */
    protected getGlobalDefaultConfigUrl(): string {
        return DEFAULT_GLOBAL_CONFIG_URL;
    }

    /**
     * 俄罗斯默认配置url
     * - 已经删除
     * @returns {string}
     */
    // protected getRussiaDefaultConfigUrl(): string {
    //     return DEFAULT_RUSSIA_CONFIG_URL;
    // }

    /**
     * 欧洲法拉克福的默认配置url
     * @returns {string}
     */
    protected getDeDefaultConfigUrl(): string {
        return DEFAULT_DE_CONFIG_URL;
    }

    /**
     * 国际化外码的url
     */
    protected getRlabDefaultConfigUrl(): string {
        return DEFAULT_RLAB_CONFIG_URL;
    }

    /**
     * H5页面默认需要的插件。
     * | OMGH5CustomPlugin | OMGH5JsErrorPlugin | OMGH5ErrorPlugin)[]}
     * @returns {IReportingStrategyPluginConstr[]}
     */
    protected getH5DefaultPluginsConstrs(): IReportingStrategyPluginConstr[] {
        return [
            OMGH5PageInitPlugin,
            OMGH5LogPlugin
        ];
    }

    /**
     * App内嵌H5默认需要的插件
     * | OMGH5CustomPlugin | OMGH5JsErrorPlugin | OMGH5ErrorPlugin)[]}
     * @returns {IReportingStrategyPluginConstr[]}
     */
    protected getAppH5DefaultPluginsConstrs(): IReportingStrategyPluginConstr[] {
        return [
            OMGAppH5PageInitPlugin,
            OMGH5LogPlugin
        ];
    }

    /**
     * 载入javascript文件，并返回window[globalVarName]对象。
     * @param {string} url
     * @param {string} globalVarName
     * @returns {Promise<T>}
     */
    protected loadScript<T>(url: string, globalVarName: string, callback?: (val: T) => void): Promise<T> {
        return new Promise<T>((resolve, reject: () => void): void => {
            let value: T = (window as any)[globalVarName];
            if (value) { // 已经存在，则不去load。
                resolve(value);
            } else {
                let isTimeout = false;
                const timeoutHandler: number = setTimeout((): void => {
                    clearTimeout(timeoutHandler);
                    isTimeout = true;
                    reject();
                }, SCRIPT_LOAD_TIMEOUT) as any;
                const scriptEle: HTMLScriptElement = document.createElement('script');
                document.documentElement.appendChild(scriptEle);
                scriptEle.onload = (event: Event): void => {
                    clearTimeout(timeoutHandler);
                    document.documentElement.removeChild(scriptEle);
                    value = (window as any)[globalVarName];
                    isTimeout ? callback && callback(value) : resolve(value);
                };
                scriptEle.onerror = (): void => {
                    GlobalUtil.dispatchOmgLog('loadScript 失败' + url);
                    clearTimeout(timeoutHandler);
                    reject();
                };
                scriptEle.crossOrigin = 'anonymous';
                scriptEle.src = url;
            }
        });
    }

    /**
     * 获取默认配置项集合。
     * @returns {IEnvConfigurationItem[]}
     */
    protected getDefaultConfigs(): IEnvConfigurationItem[] {
        const h5DefaultPluginsConstrs: IReportingStrategyPluginConstr[] = this.getH5DefaultPluginsConstrs();
        const appH5DefaultPluginsConstrs: IReportingStrategyPluginConstr[] = this.getAppH5DefaultPluginsConstrs();
        return [
            new AppH5EnvConfig(this, appH5DefaultPluginsConstrs),
            new UniqueH5EnvConfig(this, h5DefaultPluginsConstrs),
            new NormalH5EnvConfig(this, h5DefaultPluginsConstrs)
        ];
    }

    /**
     * 通过url来加载plugin并实例化。
     * @param {string} pluginsUrl
     * @returns {Promise<IPlugin[]>}
     */
    private loadPluginsByUrl(pluginsUrl: string): Promise<IPlugin[]> {
        return new Promise<IPlugin[]>((resolve: (plugins: IPlugin[]) => void): void => {
            this.loadScript<IReportingStrategyPluginConstr[]>(
                pluginsUrl,
                H5Tracker.OMG_ASYNC_PLUGINS_CONSTRS_NAME,
                /**
                 * 即使 plugins 超时，依然补上执行逻辑 [2/2]
                 */
                (pluginConstrs: IReportingStrategyPluginConstr[]) => {
                    this.methodHolderChainIsDone = true;
                    const plugins: IPlugin[] = (pluginConstrs || [])
                        .map<IPlugin>((PluginConstr: IReportingStrategyPluginConstr): IPlugin => {
                            return new PluginConstr(this);
                        });
                    this.loadPluginsBefore(this.afterAsyncPluginsLoad.bind(this), plugins);
                }
            ).then((pluginConstrs: IReportingStrategyPluginConstr[] | undefined): void => {
                try {
                    this.methodHolderChainIsDone = true;
                    pluginConstrs = pluginConstrs || [];
                    const plugins: IPlugin[] = pluginConstrs
                        .map<IPlugin>((PluginConstr: IReportingStrategyPluginConstr): IPlugin => {
                            return new PluginConstr(this);
                        });
                    resolve(plugins);
                } catch (e) {
                    console.error(e);
                    GlobalUtil.dispatchOmgLog('通过url来加载plugin并实例化 失败' + pluginsUrl);
                    resolve([new NonH5Plugin(this.getContext())]);
                }

            }, (): void => {
                resolve([new NonH5Plugin(this.getContext())]);
            });
        });
    }

    /**
     * 即使 plugins 超时，依然补上执行逻辑 [1/2]
     * @param callback
     * @param plugins
     */
    private loadPluginsBefore(callback: (extraPlugins: IPlugin[]) => void, plugins?: IPlugin[]) {
        const envConfig: IEnvConfigurationItem | undefined = (this.getContext() as IH5Context).envConfig;
        if (envConfig) { // 环境配置对象实例存在。
            // @ts-ignore
            if ((envConfig as IEnvConfigurationItemSync).getPlugins) {
                // 当前环境配置为IEnvConfigurationItemSync实例
                // @ts-ignore
                if ((envConfig as AH5EnvConfigSync).getExtraPluginsUrl) { // 为默认H5配置环境，加载额外的远程插件url
                    const pluginsUrl: string = (envConfig as AH5EnvConfigSync).getExtraPluginsUrl();
                    if (pluginsUrl) {
                        plugins ?
                            callback((envConfig as IEnvConfigurationItemSync).getPlugins().concat(plugins)) :
                            this.loadPluginsByUrl(pluginsUrl).then((extraPlugins: IPlugin[]): void => {
                                callback((envConfig as IEnvConfigurationItemSync).getPlugins().concat(extraPlugins));
                            });
                    } else {
                        GlobalUtil.dispatchOmgLog('当前环境配置为IEnvConfigurationItemSync实例 pluginsUrl 不存在' + pluginsUrl);
                        callback((envConfig as IEnvConfigurationItemSync).getPlugins());
                    }
                } else {
                    GlobalUtil.dispatchOmgLog('当前环境配置为IEnvConfigurationItemSync实例 getExtraPluginsUrl 不存在');
                    callback((envConfig as IEnvConfigurationItemSync).getPlugins());
                }
                return;
                // @ts-ignore
            } else if ((envConfig as IEnvConfigurationItemAsync).getPluginsUrl) {
                // 当前环境配置为IEnvConfigurationItemASync实例
                const pluginsUrl: string = (envConfig as IEnvConfigurationItemAsync).getPluginsUrl();
                if (pluginsUrl) {
                    plugins ? callback(plugins) : this.loadPluginsByUrl(pluginsUrl).then(callback);
                } else {
                    GlobalUtil.dispatchOmgLog('当前环境配置为IEnvConfigurationItemASync实例 pluginsUrl 不存在' + pluginsUrl);
                }
                return;
            }
        }
        GlobalUtil.dispatchOmgLog('环境配置对象实例不存在 失败');
        callback([new NonH5Plugin(this.getContext())]);
    }

    /**
     * 加载插件实现库列表。
     * @returns {Promise<IPlugin[]>}
     */
    private loadPlugins(): Promise<IPlugin[]> {
        return new Promise<IPlugin[]>((resolve: (plugins: IPlugin[]) => void): void => {
            this.loadPluginsBefore(resolve);
        });
    }

    /**
     * getCurrentEnvConfig: [1/2]
     * 获取当前环境配置信息。
     * @returns {Promise<IEnvConfigurationItem>}
     */
    private getCurrentEnvConfig(): Promise<void> {
        return new Promise<void>((resolve) => {
            const defaultConfigs: IEnvConfigurationItem[] = this.getDefaultConfigs();
            this.loadScript<IEnvConfigurationItem[]>(this.extraConfigsUrl, H5Tracker.OMG_ASYNC_CONFIGS_NAME,
                (configs: IEnvConfigurationItem[]) => {
                    // 即使超时，依然补上执行逻辑
                    this.afterAsyncConfigs1((configs || []).concat(defaultConfigs)); // 返回值暂无作用
                    this.afterAsyncConfigs2();
                })
                .then((configs: IEnvConfigurationItem[] | undefined): void => {
                    this.afterAsyncConfigs1((configs || []).concat(defaultConfigs));
                    resolve();
                }, (): void => {
                    this.afterAsyncConfigs1(defaultConfigs);
                    resolve();
                });
        });
    }

    /**
     * getCurrentEnvConfig: [2/2]
     */
    private afterAsyncConfigs1(configs: IEnvConfigurationItem[]): void {
        for (let i: number = 0, j: number = configs.length; i < j; ++i) {
            const config: IEnvConfigurationItem = configs[i];
            try {
                if (config.validate(this.getContext() as IH5Context)) { // 验证成功，环境配置命中当前环境
                    config.beforeDeliver(this).then((): void => { // 配置下发前执行。
                        Object.assign(this.context, {
                            envConfig: config
                        });
                        return;
                    });
                    break;
                }
            } catch (error) {
                GlobalUtil.dispatchOmgLog('获取当前环境配置信息 失败' + (error as ErrorEvent).message);
                console.error(error);
            }
        }
    }

    /**
     * 初始化同步插件
     */
    private initSyncPluginCollection(): void {
        const defaultConfigs: IEnvConfigurationItem[] = this.getDefaultConfigs();
        for (let i: number = 0, j: number = defaultConfigs.length; i < j; ++i) {
            const config: IEnvConfigurationItem = defaultConfigs[i];
            try {
                if (config.validate(this.getContext() as IH5Context)) { // 验证成功，环境配置命中当前环境
                    config.beforeDeliver(this).then((): void => { // 配置下发前执行。
                        Object.assign(this.context, {
                            envConfig: config
                        });
                        const envConfig: IEnvConfigurationItem | undefined =
                            (this.getContext() as IH5Context).envConfig;
                        if ((envConfig as IEnvConfigurationItemSync) &&
                            (envConfig as IEnvConfigurationItemSync).getPlugins) {
                            const plugins = (envConfig as IEnvConfigurationItemSync).getPlugins() || [];
                            plugins.forEach((plugin: IPlugin): void => {
                                try {
                                    if (!this.pluginCollection.contains(plugin)) {
                                        this.pluginCollection.register(plugin);
                                    }
                                } catch (e) {
                                    GlobalUtil.dispatchOmgLog('initSyncPluginCollection 注册插件 失败');
                                    console.error(e);
                                }
                            });
                        }
                    });
                }
            } catch (error) {
                GlobalUtil.dispatchOmgLog('获取当前环境配置信息 失败' + (error as ErrorEvent).message);
                console.error(error);
            }
        }

    }

    /**
     * initAsyncPluginCollection: [1/3]
     * 初始化插件库，先从远程获取环境配置信息，从对应的环境信息里再远程拉取插件库CDN。
     */

    protected initAsyncPluginCollection(): void {
        window.removeEventListener('load', this.initAsyncPluginCollection);
        this.getCurrentEnvConfig().then(this.afterAsyncConfigs2.bind(this));
    }

    /**
     * initAsyncPluginCollection: [2/3]
     */
    private afterAsyncConfigs2(): void {
        this.loadPlugins().then(this.afterAsyncPluginsLoad.bind(this));
    }

    /**
     * initAsyncPluginCollection: [3/3]
     * @param plugins
     */
    private afterAsyncPluginsLoad(plugins: IPlugin[]): void {
        if (!this.methodHolderChainIsDone) {
            return;
        }
        plugins.forEach((plugin: IPlugin): void => {
            try {
                if (!this.pluginCollection.contains(plugin)) {
                    this.pluginCollection.register(plugin);
                }
            } catch (e) {
                GlobalUtil.dispatchOmgLog('initAsyncPluginCollection 注册插件 失败');
                console.error(e);
            }
        });

        const chain = this.methodHolderChain;

        chain.forEach((item: IMethodHolderItem): void => {
            const rtnValue: Promise<any> | undefined = item.method.apply(this, item.arguments as any);
            if (rtnValue && rtnValue.then && item.resolve) { // 异步来做处理。
                rtnValue.then(item.resolve, item.reject);
            }
        });
        while (chain.length) {
            chain.pop();
        }
        if (this.getOmgIdHandler) {
            this.getOmgIdHandler(this.context.omgId || '');
            delete this.getOmgIdHandler;
        }
    }

    /**
     * @override
     * @inheritDoc
     */
    @MethodHold
    public execute(): void {
        this.getPluginConllection().toArray().forEach((plugin: IPlugin): void => {
            try {
                plugin.execute();
            } catch (e) {
                GlobalUtil.dispatchOmgLog('安装插件失败');
                console.error(e);
            }
        });
    }

    /**
     * @override
     * @inheritDoc
     */
    public destroy(): void {
        // unregister all of plugins
        this.getPluginConllection().unregisterAll();

        // remove configs
        const configsName: string = H5Tracker.OMG_ASYNC_CONFIGS_NAME;
        const configs: IEnvConfigurationItem[] = (window as any)[configsName] || [];
        while (configs.length) {
            configs.pop();
        }
        (window as any)[configsName] = undefined;

        // remove constrs of plugins
        const pluginConstrsName: string = H5Tracker.OMG_ASYNC_PLUGINS_CONSTRS_NAME;
        const pluginsConstrs: IReportingStrategyPluginConstr[] = (window as any)[pluginConstrsName] || [];
        while (pluginsConstrs.length) {
            pluginsConstrs.pop();
        }
        (window as any)[pluginConstrsName] = undefined;

        // remove sequence under each viewport id.
        const prefix: string = SequencedEvent.OMG_SEQUENCE_NAME_PREFIX;
        const suffix: string = SequencedEvent.OMG_SEQUENCE_NAME_SUFFIX;
        (window as any)[`${ prefix }${ (this.getContext() as IH5Context).viewportId }${ suffix }`] = undefined;

        // remove event black list
        const bn: string = ReportCallbackUtil.OMG_EVENT_BLACKLIST_NAME;
        const eventBlacklist: string[] = (window as any)[bn] || [];
        while (eventBlacklist.length) {
            eventBlacklist.pop();
        }
        (window as any)[bn] = undefined;

        // remove all of response callbacks.
        (window as any)[ReportCallbackUtil.OMG_CALLBACK_OBJ_NAME] = undefined;

        // unbind 'pagehide' event listener
        window.removeEventListener('pagehide', this.pagehideHandler);
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IContext}
     */
    public getContext(): IH5Context {
        return this.context;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IPluginCollection}
     */
    public getPluginConllection(): IPluginCollection {
        return this.pluginCollection;
    }

    /**
     * @override
     * @inheritDoc
     * @param {ITrackerConfiguration} config
     */
    public setConfig(config: ITrackerConfiguration): void {
        if (config.hasOwnProperty('appKey')) {
            if (GlobalUtil.isEmptyString(config.appKey)) {  // 设置app key
                if (!this.context.appKey) {
                    this.init(config);
                }
            } else {
                GlobalUtil.dispatchOmgLog('setConfig appKey 参数 不可设置为空');
                console.error('[Omega]setConfig appKey 参数 不可设置为空');
                // @ts-ignore
                config.appKey = this.context.appKey;
            }
        }
        if (this.context.attrs && config.attrs) { // 当上下文中已经存在attrs时，又设置了全局attrs，则进行合并。
            // 现将 attrs 合并至 context.attrs
            Object.assign(this.context.attrs, config.attrs);
            // 再将 context.attrs 合并至  attrs，以防止 被覆盖
            Object.assign(config.attrs, this.context.attrs);
        }
        Object.assign(this.context, config);

    }

    /**
     * fix the track event args.
     * @param {string} eventId
     * @param {string | IAttrs | EventTarget | TrackedSuccessfulCallback} eventLabel
     * @param {IAttrs | EventTarget | TrackedSuccessfulCallback} attrs
     * @param {EventTarget | TrackedSuccessfulCallback} target
     * @param {TrackedSuccessfulCallback} cb
     * @returns {ITrackedEventArgs}
     */
    protected fixEventArgs(eventId: string, eventLabel?: string | IAttrs | EventTarget | TrackedSuccessfulCallback,
                           attrs?: IAttrs | EventTarget | TrackedSuccessfulCallback,
                           target?: EventTarget | TrackedSuccessfulCallback,
                           cb?: TrackedSuccessfulCallback): ITrackedEventArgs {
        let newLabel: string = '';
        let newAttrs: IAttrs = {};
        let newCallback: TrackedSuccessfulCallback | undefined;
        for (let i: number = 1, j: number = arguments.length; i < j; ++i) { // 判断可选参数情况。
            const argValue: any = arguments[i];
            const argType: string = Object.prototype.toString.call(argValue);
            if (argType === '[object String]') { // event label
                newLabel = argValue;
            } else if (argType === '[object Object]') { // attrs
                newAttrs = argValue;
            } else if (argType === '[object Function]') { // callback
                newCallback = argValue;
            }
        }
        this.trackHandlers.length && this.mergeTrackHandler(eventId, newAttrs, target as EventTarget);
        return {
            eventId: eventId,
            attrs: {
                ...(newAttrs || {}),
                _label: `${ newLabel || '' }`
            },
            creatInfo: {
                appKey: this.context.appKey
            },
            cb: newCallback
        };
    }

    protected mergeTrackHandler(eventId: string, attrs: IAttrs, target: EventTarget) {
        this.trackHandlers.forEach((handler) => {
            const parsedAttr = handler.call(target, eventId, attrs, target);
            if (Object.prototype.toString.call(parsedAttr) === '[object Object]') {
                Object.assign(attrs, parsedAttr);
            }
        });
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} eventId
     * @param {string} eventLabel
     * @param {IAttrs} attrs
     * @param {EventTarget} target
     * @param {TrackedSuccessfulCallback} cb
     * @returns {Promise<ITrackedEvent>}
     */
    public trackEvent(eventId: string,
                      eventLabel?: string | IAttrs | EventTarget | TrackedSuccessfulCallback,
                      attrs?: IAttrs | EventTarget | TrackedSuccessfulCallback,
                      target?: EventTarget | TrackedSuccessfulCallback,
                      cb?: TrackedSuccessfulCallback): Promise<ITrackedEvent> {
        // @ts-ignore
        if (eventLabel && eventLabel.err && Object.prototype.toString.call(eventLabel.err) === '[object Error]') {
            // @ts-ignore
            this.trackError(eventId, eventLabel.err!.code, eventLabel.err!.stack, { message: eventLabel.err.message });
        }
        const eventArgs = this.fixEventArgs(eventId, eventLabel, attrs, target, cb);
        return this.reportCustomEvent(eventArgs);
    }

    /**
     * 上报自定义事件
     */
    @MethodHoldAsync
    public reportCustomEvent(eventArgs: ITrackedEventArgs): Promise<ITrackedEvent> {
        return this.trackEventByPlugin(PluginUtils.PLUGIN_NAME_CUSTOM, eventArgs);
    }

    /**
     * @override
     * @inheritDoc
     * @param {IAttrs} attrs
     * @returns {Promise<ITrackedEvent>}
     */
    @MethodHoldAsync
    public sendPageView(attrs?: IAttrs): Promise<ITrackedEvent> {
        this.setConfig({
            url: window.location.href
        });
        return this.trackEventByPlugin(
            PluginUtils.PLUGIN_NAME_PAGE_VIEW,
            this.fixEventArgs(PluginUtils.EVENT_ID_PAGE_VIEW, '', attrs, window)
        );
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} name
     * @param {string | number} code
     * @param {string} msg
     * @param {IAttrs} attrs
     * @returns {Promise<ITrackedEvent>}
     */
    @MethodHoldAsync
    public trackError(name: string, code: string | number, msg: string, attrs?: IAttrs): Promise<ITrackedEvent> {
        return this.trackEventByPlugin(
            PluginUtils.PLUGIN_NAME_ERROR,
            this.fixEventArgs(PluginUtils.EVENT_ID_ERROR, msg, {
                ...(attrs || {}),
                name: name,
                code: code
            })
        );
    }
    /**
     * 上报业务页面的完成时间
     * - 参数自定义
     */
    public trackPageCompleted(attrs?: IAttrs) {
        this.trackEvent('pub_page_completed_load_sw', attrs);
    }
    /**
     * 根据指定的plugin上报相应的数据。
     * 多个相同plugin会分别实例化执行。
     * @param {string} pluginName
     * @param {ITrackedEventArgs} eventArgs
     * @returns {Promise<ITrackedEvent>}
     */
    private trackEventByPlugin(pluginName: string, eventArgs: ITrackedEventArgs): Promise<ITrackedEvent> {
        const pluginReports: Array<[IPlugin, ReportFucHandler, ITrackedEventArgs]> = [];
        this.getPluginConllection().toArray().forEach((plugin: IPlugin): void => {
            if (plugin.getName() === pluginName) {
                pluginReports.push([
                    plugin,
                    (plugin as IReportingStrategyPlugin).report,
                    eventArgs
                ]);
            }
        });
        return Promise.all(pluginReports.map<Promise<ITrackedEvent>>(
            (pluginReportItem: [IPlugin, ReportFucHandler, ITrackedEventArgs]): Promise<ITrackedEvent> => {
                return pluginReportItem[1].call(pluginReportItem[0], pluginReportItem[2]);
            }
        )).then((events: ITrackedEvent[]): ITrackedEvent => events[0]);
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} userName
     */
    public setUserName(userName: string): void {
        this.setConfig({
            userName: userName
        });
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} userId
     */
    public setUserId(userId: string): void {
        this.setConfig({
            userId: userId
        });
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} tel
     * @param {string} countryCallingCode
     */
    public setTelephone(tel: string, countryCallingCode?: string): void {
        console.error('setTelephone API已移除');
        // if (countryCallingCode) {
        //     this.setConfig({
        //         telephone: tel,
        //         countryCallingCode: countryCallingCode
        //     });
        // } else {
        //     this.setConfig({
        //         telephone: tel
        //     });
        // }
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} openid
     */
    public setOpenId(openid: string): void {
        if ((this.getContext() as IH5Context).unique) { // 只有unique为true时，才能够设置openid
            Object.assign(this.context, {
                openId: openid
            });
        }
    }

    /**
     * @override
     * @inheritDoc
     * @param {number} cityId
     */
    public setCityId(cityId: number): void {
        this.setConfig({ cityId: cityId });
    }

    /**
     * @override
     * @inheritDoc
     * @param {number} lng
     * @param {number} lat
     */
    public setLocation(lng: number, lat: number): void;

    /**
     * @override
     * @inheritDoc
     * @param {IGeoLocation} geoLocation
     */
    public setLocation(geoLocation: IGeoLocation): void;

    /**
     * override <code>setLocation(lng: number, lat: number): void</code> and
     *          <code>setLocation(geoLocation: IGeoLocation) :void</code>
     * @param {number | IGeoLocation} arg1
     * @param {number} arg2
     */
    public setLocation(arg1: number | IGeoLocation, arg2?: number): void {
        console.error('setLocation API已删除');
        // if (typeof arg1 === 'number') { // the first arg is longitude
        //     this.setConfig({
        //         longitude: arg1,
        //         latitude: arg2
        //     });
        // } else { // the first arg is IGeoLocation
        //     this.setConfig(arg1);
        // }
    }

    /**
     * @override
     * @inheritDoc
     * @returns {string}
     */
    public getOmgid(): string | undefined;

    /**
     * @override
     * @inheritDoc
     * @param {GetOmgIdCallback} cb
     */
    public getOmgid(cb: GetOmgIdCallback): void;

    /**
     * override methods <code>getOmgid(): string</code> and <code>getOmgid(cb: GetOmgIdCallback): void</code>
     * @param {GetOmgIdCallback} cb
     * @returns {void | string}
     */
    public getOmgid(cb?: GetOmgIdCallback): void | string | undefined {
        const omgId: string = (this.getContext() as IH5Context).omgId || '';
        if (cb) {
            if (omgId) {
                cb(omgId);
            } else {
                this.getOmgIdHandler = cb;
            }
        } else {
            return omgId;
        }
    }

    /**
     * @override
     * @inheritDoc
     * @param {GetOmgIdCallback} cb
     */
    public getClientId(cb: GetOmgIdCallback): void {
        this.getOmgid(cb);
    }

    /**
     * @override
     * @inheritDoc
     * @param {IAutoClickCover} cover
     */
    public enableClickAutoTracker(cover: IAutoClickCover, ifChildNode?: boolean): void {
        this.clickedCover = cover;
        this.clickedCoverIsChild = ifChildNode || false;
    }

    /**
     * @override
     * @inheritDoc
     * @param {IAutoClickCover} cover
     */
    public enableTouchAutoTracker(cover: IAutoClickCover, ifChildNode?: boolean): void {
        this.enableClickAutoTracker(cover, ifChildNode);
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IAutoClickCover}
     */
    public getClickedCover(): IAutoClickCover | undefined {
        return this.clickedCover;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public getClickedCoverChild(): boolean | undefined {
        return this.clickedCoverIsChild;
    }

    /**
     * @override
     * @inheritDoc
     * @param {IClickFilter} filter
     */
    public enableClickFilterTracker(filter: IClickFilter): void {
        this.clickedFilter = filter;
    }

    /**
     * @override
     * @inheritDoc
     * @param {IClickFilter} filter
     */
    public enableTouchFilterTracker(filter: IClickFilter): void {
        this.enableClickFilterTracker(filter);
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IClickFilter | undefined}
     */
    public getClickedFilter(): IClickFilter | undefined {
        return this.clickedFilter;
    }

    /*======================================== deprecated methods as below ========================================*/

    /**
     * @override
     * @inheritDoc
     */
    public disableAutoClick(): void {
        this.setConfig({
            autoClick: false
        });
    }

    /**
     * @override
     * @inheritDoc
     * @returns {string}
     */
    public sid(): string {
        return (this.getContext() as IH5Context).viewportId;
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} appKey
     */
    public setProduct(appKey: string): void {
        this.setConfig({ appKey: appKey });
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} channel
     */
    public setChannel(channel: string): void {
        this.setConfig({ attrs: { channel: channel } });
    }

    /**
     * @override
     * @inheritDoc
     * @param {boolean} mode
     */
    public enableDebugMode(mode: boolean): void {
        /* deprecated and blank method */
    }

    /**
     * @override
     * @inheritDoc
     */
    public trackPerformance(): void {
        /* deprecated and blank method */
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} label
     * @param {number} time
     * @param {string} deprecatedVersion
     * @param {IAttrs} attrs
     * @param {number} sample
     * @returns {Promise<ITrackedEvent>}
     */
    public trackTiming(label: string, time: number, deprecatedVersion?: string,
                       attrs?: IAttrs, sample?: number): Promise<ITrackedEvent> {
        /* deprecated and a simple implements */
        return this.trackEvent('OMGH5CustomTiming');
    }

    /**
     * @override
     * @inheritDoc
     * @param {string} markLabel
     */
    public markTimePoint(markLabel: string): void { /* deprecated and blank method */
    }

    /**
     * @override
     * @inheritDoc
     * @param {(errorMessage: string, defaultAttrs: ITrackedEventData, attrs: IAttrs) => void} handler
     */
    public onJsError(handler: (errorMessage: string, defaultAttrs: ITrackedEventData, attrs: IAttrs) => void): void {
        /* deprecated and blank method */
    }

    /**
     * @override
     * @inheritDoc
     * @param {string | number} state
     */
    public setState(state: string | number): void { /* deprecated and blank method */
    }

    /**
     * @override
     * @inheritDoc
     * @param {TrackedHandler<T extends IPlanObject>} handler
     */
    public addTrackHandler<T extends IPlanObject>(handler: TrackedHandler<T>): () => void {
        /* deprecated and blank method */
        if (Object.prototype.toString.call(handler) !== '[object Function]') {
            return () => {
                //
            };
        }
        this.trackHandlers.push(handler);
        return () => {
            return this.trackHandlers.splice(this.trackHandlers.findIndex((item) => item === handler), 1);
        };
    }
}
